/**
 * @module features/client-device-management/components/ClientLogoAvatar
 * @description Zero-CLS organization logo avatar with OWASP protocol whitelisting
 * and automatic 2-letter monogram fallback when an image is absent or fails to load.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { sanitizeLogoUrl } from "../types";

export interface ClientLogoAvatarProps {
  orgName: string;
  logoUrl?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZE_CLASSES: Record<
  NonNullable<ClientLogoAvatarProps["size"]>,
  { box: string; px: number; text: string }
> = {
  sm: { box: "h-9 w-9 rounded-xl", px: 36, text: "text-xs" },
  md: { box: "h-10 w-10 rounded-xl", px: 40, text: "text-xs" },
  lg: { box: "h-12 w-12 rounded-2xl", px: 48, text: "text-sm" },
};

/**
 * Renders a square organization logo reserving explicit dimensions (`aspect-square` +
 * `width`/`height`) to prevent Cumulative Layout Shift (CLS = 0.00).
 */
export function ClientLogoAvatar({
  orgName,
  logoUrl,
  size = "md",
  className,
}: ClientLogoAvatarProps) {
  const safeUrl = React.useMemo(() => sanitizeLogoUrl(logoUrl), [logoUrl]);
  const [imgFailed, setImgFailed] = React.useState(false);

  // Reset failure flag whenever the underlying URL changes
  React.useEffect(() => {
    setImgFailed(false);
  }, [safeUrl]);

  const monogram = React.useMemo(() => {
    const trimmed = (orgName || "").trim();
    if (!trimmed) return "CL";
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
  }, [orgName]);

  const dims = SIZE_CLASSES[size];

  if (safeUrl && !imgFailed) {
    return (
      <img
        src={safeUrl}
        alt={`${orgName} logo`}
        width={dims.px}
        height={dims.px}
        loading="lazy"
        onError={() => setImgFailed(true)}
        className={cn(
          dims.box,
          "aspect-square object-contain border border-border/80 bg-card p-1 shrink-0 shadow-card",
          className
        )}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className={cn(
        dims.box,
        dims.text,
        "aspect-square border border-border/80 bg-muted text-foreground flex items-center justify-center font-semibold shrink-0 shadow-card select-none",
        className
      )}
    >
      {monogram}
    </div>
  );
}

